import React, { useEffect, useState } from 'react';
import { Student } from '../../pages/StudentManagement';

interface AddStudentModalProps {
  visible: boolean;
  initialValues?: Student;
  onClose: () => void;
  onStudentAdded: () => void;
}

const AddStudentModal: React.FC<AddStudentModalProps> = ({
  visible = false,
  initialValues,
  onClose,
  onStudentAdded,
}) => {
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    fullName: initialValues?.fullName || '',
    universityEmail: initialValues?.universityEmail || '',
    institutionName: initialValues?.institutionName || '',
    contactNumber: initialValues?.contactNumber || '',
    studentType: initialValues?.studentType || '',
    researchTitle: initialValues?.researchTitle || '',
    researchAbstract: initialValues?.researchAbstract || '',
    researchStartDate: initialValues?.researchStartDate || '',
    researchEndDate: initialValues?.researchEndDate || '',
  });
  const [shareableCode, setShareableCode] = useState('');
  const [importedStudentId, setImportedStudentId] = useState(initialValues?.userId || '');
  const [isImportStudentModalOpen, setIsImportStudentModalOpen] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const isEdit = !!initialValues;

  useEffect(() => {
    if (initialValues) {
      let startDate;
      let endDate;
      if (initialValues.researchStartDate && initialValues.researchEndDate) {
        startDate = new Date(initialValues.researchStartDate).toISOString().split('T')[0];
        endDate = new Date(initialValues.researchEndDate).toISOString().split('T')[0];
      } else {
        startDate = '';
        endDate = '';
      }

      setFormValues({
        fullName: initialValues.fullName,
        universityEmail: initialValues.universityEmail,
        institutionName: initialValues.institutionName,
        contactNumber: initialValues.contactNumber || '',
        studentType: initialValues.studentType || '',
        researchTitle: initialValues.researchTitle || '',
        researchAbstract: initialValues.researchAbstract || '',
        researchStartDate: startDate,
        researchEndDate: endDate,
      });
    } else {
      setFormValues({
        fullName: '',
        universityEmail: '',
        institutionName: '',
        contactNumber: '',
        studentType: '',
        researchTitle: '',
        researchAbstract: '',
        researchStartDate: '',
        researchEndDate: '',
      });
    }
  }, [initialValues]);

  const handleSubmit = async () => {
    setLoading(true);
    console.log('Imported Student ID:', importedStudentId);

    const payload = {
      ...formValues,
      researchStartDate: formValues.researchStartDate
        ? new Date(formValues.researchStartDate).toISOString()
        : '',
      researchEndDate: formValues.researchEndDate
        ? new Date(formValues.researchEndDate).toISOString()
        : '',
      userId: importedStudentId,
    };
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/students/${
          isEdit ? `update/${initialValues.studentId}` : 'add'
        }`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add student');
      }

      setFormValues({
        fullName: '',
        universityEmail: '',
        institutionName: '',
        contactNumber: '',
        studentType: '',
        researchTitle: '',
        researchAbstract: '',
        researchStartDate: '',
        researchEndDate: '',
      });
      onStudentAdded();
      onClose();
    } catch (error) {
      console.log(error);
      alert('Failed to add student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImportStudent = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/importcode/import/${shareableCode}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to import student');
      }

      const data = await response.json();
      setImportedStudentId(data._id);
      setFormValues({
        fullName: data.fullName,
        universityEmail: data.universityEmail,
        institutionName: data.institutionName,
        contactNumber: data.contactNumber,
        studentType: data.studentType,
        researchTitle: data.researchTitle,
        researchAbstract: data.researchAbstract,
        researchStartDate: data.researchStartDate,
        researchEndDate: data.researchEndDate,
      });
      setIsImportStudentModalOpen(false);
    } catch (error) {
      console.log(error);
      alert('Failed to import student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${
        visible ? '' : 'hidden'
      }`}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isEdit ? 'Edit' : 'Add'} Student</h2>

          <div>
            {!isEdit && (
              <button
                className="text-sm bg-blue-500 text-white me-4 px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                onClick={() => setIsImportStudentModalOpen(true)}
              >
                Import Student
              </button>
            )}
            <button onClick={onClose}>
              <i className="fa fa-x text-xs" />
            </button>
          </div>
        </div>
        <form className="overflow-auto max-h-[75vh]">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Full Name</label>
            <input
              type="text"
              name="fullName"
              value={formValues.fullName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
              readOnly={!!importedStudentId}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">University Email</label>
            <input
              type="email"
              name="universityEmail"
              value={formValues.universityEmail}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
              readOnly={!!importedStudentId}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Institution Name</label>
            <input
              type="text"
              name="institutionName"
              value={formValues.institutionName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
              readOnly={!!importedStudentId}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Contact Number</label>
            <input
              type="text"
              name="contactNumber"
              value={formValues.contactNumber}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Student Type</label>
            <input
              type="text"
              name="studentType"
              value={formValues.studentType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Research Title</label>
            <input
              type="text"
              name="researchTitle"
              value={formValues.researchTitle}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Research Abstract</label>
            <textarea
              name="researchAbstract"
              value={formValues.researchAbstract}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Research Start Date</label>
            <input
              type="date"
              name="researchStartDate"
              value={formValues.researchStartDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Research End Date</label>
            <input
              type="date"
              name="researchEndDate"
              value={formValues.researchEndDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-2 px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className={`text-sm px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={loading}
            >
              {loading ? 'Adding...' : `${isEdit ? 'Edit' : 'Add'} Student`}
            </button>
          </div>
        </form>
      </div>

      {isImportStudentModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded-lg w-96">
            <h2 className="text-xl font-semibold mb-4">Import Student</h2>
            <input
              type="text"
              value={shareableCode}
              onChange={e => setShareableCode(e.target.value)}
              placeholder="Enter shareable code"
              className="w-full p-2 border border-gray-300 rounded-md mb-4"
            />
            <div className="flex justify-between">
              <button
                onClick={handleImportStudent}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Import
              </button>
              <button
                onClick={() => setIsImportStudentModalOpen(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddStudentModal;
